import React, { useEffect, useState } from "react";
import styles from "./ApprovalLineModal.module.css";
import api from "../../api"; // ✅ 직원 API 사용
import { computeDefaultApprovalLine } from "./computeDefaultApprovalLine";
import { isActiveStaff } from "../../utils/contractEnded";

// ✅ initialApprovers: 페이지가 이미 자동 생성해둔 결재선(ApprovalLine.jsx가 마운트
// 시 채워둔 값) — 있으면 그대로 초기값으로 쓰고(수정 진입), 없을 때만(자동 생성 실패
// 등 예외 상황) computeDefaultApprovalLine으로 새로 계산한다.
export default function ApprovalLineModal({ onClose, onSelect, documentType, initialApprovers = [] }) {
  console.log("✅ ApprovalLineModal 열림");

  const [selectedUsers, setSelectedUsers] = useState(initialApprovers);   // ✅ 오른쪽 확정 결재자
  const [tempUsers, setTempUsers] = useState([]);           // ✅ 왼쪽에서 임시 선택
  const [searchTerm, setSearchTerm] = useState("");

  // ✅ 직원 조직도 데이터
  const [orgData, setOrgData] = useState(null);
  const [expandedDepts, setExpandedDepts] = useState(["전체"]);

  // ✅ 직원 연락망 API 로드(왼쪽 조직도 트리용) + initialApprovers가 비어있을 때만
  // 기본 결재선 폴백 계산
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/staff-contacts/");

        const deptMap = {};
        data.forEach((item, index) => {
          // 필터링: 빈 이름, "테스트" 포함, 계약종료(서버 판정 is_contract_ended) 제외
          // (예전엔 "남유진(계약종료)" 이름 완전일치 — 이름 복구 시 풀리므로 플래그로 교체)
          if (
            !item.name ||
            item.name.trim() === "" ||
            item.name.includes("테스트") ||
            !isActiveStaff(item)
          ) {
            return;
          }

          const deptName = item.position || "기타";

          if (!deptMap[deptName]) {
            deptMap[deptName] = {
              name: deptName,
              count: 0,
              users: [],
            };
          }

          const userId = item.user_id || item.id || `user_${index}_${item.name}`;

          deptMap[deptName].users.push({
            id: userId,
            name: item.name,
            position: item.position || "직원",
          });

          deptMap[deptName].count += 1;
        });

        const tree = {
          name: "전체",
          count: data.length,
          children: Object.values(deptMap),
        };

        setOrgData(tree);
        setExpandedDepts(["전체"]);

        if (!initialApprovers || initialApprovers.length === 0) {
          const defaultApprovers = await computeDefaultApprovalLine(documentType);
          console.log("✅ 설정된 기본 결재선(폴백):", defaultApprovers);
          setSelectedUsers(defaultApprovers);
        }
      } catch (err) {
        console.error("❌ 직원 조직도 API 로드 실패:", err);
      }
    })();
  }, []);

  // ✅ 검색어 변경 시 매칭되는 부서 자동 확장
  useEffect(() => {
    if (!searchTerm || !orgData) return;
    
    const expandedDeptNames = ["전체"];
    
    const findMatchingDepts = (dept) => {
      if (!dept) return;
      
      const hasMatchingUsers = dept.users?.some((u) =>
        u.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      if (hasMatchingUsers) {
        expandedDeptNames.push(dept.name);
      }
      
      if (dept.children) {
        dept.children.forEach(findMatchingDepts);
      }
    };
    
    findMatchingDepts(orgData);
    setExpandedDepts(expandedDeptNames);
  }, [searchTerm, orgData]);

  // ✅ 부서 토글
  const toggleDept = (deptName) => {
    setExpandedDepts((prev) =>
      prev.includes(deptName)
        ? prev.filter((name) => name !== deptName)
        : [...prev, deptName]
    );
  };

  // ✅ ✅ ✅ 왼쪽 직원 클릭 → 임시 선택만
  const handleTempUserClick = (user) => {
    console.log("클릭한 사용자:", user);
    setTempUsers((prev) => {
      console.log("현재 tempUsers:", prev);
      const exists = prev.find((u) => u.id === user.id);
      console.log("이미 존재?", exists);
      if (exists) {
        return prev.filter((u) => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };

  // ✅ ✅ ✅ [추가] 버튼 → 오른쪽으로 이동
  const handleAddUsers = () => {
    setSelectedUsers((prev) => {
      const merged = [...prev];
  
      tempUsers.forEach((u) => {
        if (!merged.find((m) => m.id === u.id)) {
          merged.push({ ...u, status: "결재" });
        }
      });
  
      return merged;
    });
  
    setTempUsers([]);   // ✅ [추가] 후 왼쪽 선택 초기화
  };

  // ✅ 오른쪽 [제외] 버튼
  const handleRemoveSelected = (userId) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  // ✅ 위로 이동
  const handleMoveUp = (index) => {
    if (index === 0) return; // 이미 맨 위
    setSelectedUsers((prev) => {
      const newArray = [...prev];
      [newArray[index - 1], newArray[index]] = [newArray[index], newArray[index - 1]];
      return newArray;
    });
  };

  // ✅ 아래로 이동
  const handleMoveDown = (index) => {
    setSelectedUsers((prev) => {
      if (index === prev.length - 1) return prev; // 이미 맨 아래
      const newArray = [...prev];
      [newArray[index], newArray[index + 1]] = [newArray[index + 1], newArray[index]];
      return newArray;
    });
  };

  // ✅ 결재 상태 변경
  const handleStatusChange = (userId, newStatus) => {
    setSelectedUsers((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, status: newStatus } : u))
    );
  };

  // ✅ 최종 등록
  const handleSubmit = () => {
    console.log("✅ 등록할 결재자:", selectedUsers);
    onSelect(selectedUsers);
    onClose();  // ✅ 모달 닫기
  };

  // ✅ 조직도 렌더링
  const renderDept = (dept, level = 0) => {
    if (!dept) return null;

    const isExpanded = expandedDepts.includes(dept.name);
    const hasChildren = dept.children && dept.children.length > 0;
    const hasUsers = dept.users && dept.users.length > 0;

    const filteredUsers = hasUsers
      ? dept.users.filter((u) =>
          u.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : [];

    return (
      <div key={dept.name} style={{ marginLeft: level * 15 }}>
        <div
          className={styles.deptItem}
          onClick={() => (hasChildren || hasUsers) && toggleDept(dept.name)}
        >
          {(hasChildren || hasUsers) && (
            <span className={styles.arrow}>
              {isExpanded ? "▼" : "▶"}
            </span>
          )}
          <span className={styles.folderIcon}>📁</span>
          <span className={styles.deptName}>{dept.name}</span>
          <span className={styles.deptCount}>{dept.count}</span>
        </div>

        {/* ✅ 왼쪽 직원 리스트 */}
        {isExpanded &&
          hasUsers &&
          filteredUsers.map((user) => (
            <div
              key={user.id}
              className={`${styles.userItem} ${
                tempUsers.find((u) => u.id === user.id)
                  ? styles.selected
                  : ""
              }`}
              onClick={() => handleTempUserClick(user)}
              style={{ marginLeft: (level + 1) * 15 }}
            >
              <span className={styles.userIcon}>👤</span>
              <span className={styles.userName}>{user.name}</span>
              <span className={styles.userEmail}>
                ({user.position})
              </span>
            </div>
          ))}

        {isExpanded &&
          hasChildren &&
          dept.children.map((child) =>
            renderDept(child, level + 1)
          )}
      </div>
    );
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>결재자 수정</h2>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        <div className={styles.content}>
          {/* ✅ 왼쪽: 직원 목록 */}
          <div className={styles.leftPanel}>
            <div className={styles.panelTitle}>직원 목록</div>

            <div className={styles.searchBox}>
              <input
                type="text"
                placeholder="이름 또는 직위로 검색"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              <button className={styles.searchBtn}>🔍</button>
            </div>

            <div className={styles.orgTree}>
              {orgData ? renderDept(orgData) : "조직도 로딩중..."}
            </div>
          </div>

          {/* ✅ 가운데 버튼 영역 */}
          <div className={styles.middleBtns}>
            <button onClick={handleAddUsers}>추가</button>
            <button onClick={() => setTempUsers([])}>삭제</button>
          </div>

          {/* ✅ 오른쪽: 선택된 결재자 */}
          <div className={styles.rightPanel}>
            <div className={styles.panelTitle}>
              적용대상 <span className={styles.count}>{selectedUsers.length}명</span>
            </div>

            <table className={styles.selectedTable}>
              <thead>
                <tr>
                  <th>번호</th>
                  <th>이름</th>
                  <th>직위</th>
                  <th>결재상태</th>
                  <th>동작</th>
                </tr>
              </thead>
              <tbody>
                {selectedUsers.map((user, idx) => (
                  <tr key={user.id}>
                    <td>{idx + 1}</td>
                    <td>{user.name}</td>
                    <td>{user.position}</td>

                    <td>
                      <div className={styles.badgeWrap}>
                        <button
                          className={
                            user.status === "기안"
                              ? styles.draftYellow
                              : styles.approvalGray
                          }
                          onClick={() => handleStatusChange(user.id, "기안")}
                        >
                          기안
                        </button>
                        <button
                          className={
                            user.status === "결재"
                              ? styles.approval
                              : styles.approvalGray
                          }
                          onClick={() => handleStatusChange(user.id, "결재")}
                        >
                          결재
                        </button>
                        <button
                          className={
                            user.status === "합의"
                              ? styles.agreeBlue
                              : styles.approvalGray
                          }
                          onClick={() => handleStatusChange(user.id, "합의")}
                        >
                          합의
                        </button>
                      </div>
                    </td>

                    <td>
                      <div className={styles.actionBtns}>
                        <button
                          className={styles.moveBtn}
                          onClick={() => handleMoveUp(idx)}
                          disabled={idx === 0}
                        >
                          ↑
                        </button>
                        <button
                          className={styles.moveBtn}
                          onClick={() => handleMoveDown(idx)}
                          disabled={idx === selectedUsers.length - 1}
                        >
                          ↓
                        </button>
                        <button
                          className={styles.statusBtn}
                          onClick={() => handleRemoveSelected(user.id)}
                        >
                          제외
                        </button>
                      </div>
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose}>취소</button>
          <button className={styles.submitBtn} onClick={handleSubmit}>등록</button>
        </div>
      </div>
    </div>
  );
}